import React, { ChangeEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { IDeviceChannel } from 'lavva.exalushome/build/js/Services/Devices/IDeviceChannel';
import { groupBy } from 'lodash';
import { Header, InputMui, NavHeader, Page } from '../../../../../../../components';
import { useDebounce } from '../../../../../../../hooks/debounce/use-debounce';
import { ROUTES } from '../../../../../../../routes/routes';
import { ConditionSourceType } from '../../../../../../../types/actions';
import { DeviceChannelsBox } from '../../../../../components/channel/device-channels-box';
import { useChannels } from '../../../../channel-list/hooks/use-channels';
import { DeviceChannelsGrouped } from '../../../../channel-list/types';
import { useExalusCreateActionContext } from '../../../context';
import { useActionConditions } from '../../../hooks/use-action-conditions';

const ExalusDeviceSource: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('action');
  const { t: tc } = useTranslation('common');
  const { sourceType, setChannels } = useExalusCreateActionContext();
  const { getSourceChannels } = useActionConditions();
  const { groupedChannels } = useChannels();
  const { inputValue, inputQuery, setInputValue } = useDebounce();

  const handleChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
    setInputValue(target.value);
  };

  const devicesChannelsGrouped = useMemo(() => {
    const channelList = getSourceChannels(groupedChannels).filter((x) =>
      x.Name.toLowerCase().includes(inputQuery.toLowerCase()),
    );
    const grouped: Record<string, IDeviceChannel[]> = groupBy(channelList, (x) => x.GetDevice().Guid);

    return Object.entries(grouped).map(([id, channels]) => ({
      id,
      deviceName: channels[0].GetDevice().Name,
      channels: channels,
    }));
  }, [sourceType, groupedChannels, inputQuery]);

  const handleExalusChannelItemClick = (channel: IDeviceChannel) => {
    setChannels([channel]);
    const id = new URLSearchParams(location.search).get('id');
    navigate(`${ROUTES.ExalusDeviceParameters()}${id ? `?id=${id}` : ''}`);
  };

  return (
    <Page
      isStickyHeader
      header={
        <>
          <NavHeader title={t('action.create.conditions.sources.header')} />
          <Header column isUnderline>
            <InputMui
              value={inputValue}
              autoFocus
              onChange={handleChange}
              placeholder={tc('search')}
              sx={{ marginBottom: 0 }}
              clearEnabled
            />
          </Header>
        </>
      }
    >
      <div className="grid-list-24">
        {devicesChannelsGrouped.map((deviceChannelsGrouped: DeviceChannelsGrouped) => (
          <DeviceChannelsBox
            key={deviceChannelsGrouped.id}
            deviceChannelsGrouped={deviceChannelsGrouped}
            isListItem
            handleExalusChannelItemClick={handleExalusChannelItemClick}
            allCollapsedOpen={!!inputQuery}
            hideControls
            isChannelNumber={
              sourceType === ConditionSourceType.Remotes ||
              sourceType === ConditionSourceType.BlindRemotes ||
              sourceType === ConditionSourceType.FacadeRemotes
            }
          />
        ))}
      </div>
    </Page>
  );
};

export default ExalusDeviceSource;
